// ---------------------------------------------------------------------------
// The header for every screen, rooms included.
//
// It is deliberately quiet. Nothing up here is allowed to compete with a
// running clock or a tile row, which is why it was once left off the room
// screens altogether — but the cost of that was a room with no way home, no
// account entry and no theme control, on the screens a player spends the whole
// game on. It is here now, in the same shape it has everywhere else.
//
// Two seams exist for the room and nothing else uses them. `homeGuard` lets the
// room stop the brand link and ask first, because leaving mid-game is not an
// action to take on one stray tap. `trailing` is where the room hangs its chat
// button on a phone.
//
// The theme control lives here rather than on a settings screen because the
// light variant is a per-device reading preference, not a game setting.
//
// Account entry is here too, rather than at the foot of the home screen where
// it used to sit. Two things follow from that: signing in is reachable from the
// browser and the solo screens as well, and the home screen no longer ends on a
// control nobody was looking for.
//
// THE RIGHT-HAND SIDE IS ONE BUTTON, and that is a correction. It used to be a
// name, a handle, a Sign out and the theme control, four things sharing the
// right half of a 360px phone. Each was already at its minimum, so the only
// give left was in the handle: `@isaac` — five characters — came out as `@isa…`
// on a real phone. Everything that was out there is now inside the menu behind
// the avatar, where the handle has a row to itself and cannot be cut at all.
//
// The theme control was a 74px WORD rather than a sun-and-moon icon, and the
// argument for that was sound: the third state is "follow the system" and no
// single icon says that unambiguously, least of all one cycling through three
// meanings. That argument is answered rather than abandoned. The menu holds a
// row of three labelled options, so "System" is *named* beside Dark and Light
// instead of being implied by a glyph, and each carries an icon as decoration
// rather than as the whole message. A cycling icon button would still be wrong.
//
// The row never wraps. The brand shrinks and truncates; the trailing slot and
// the avatar keep their width, and there is no longer anything else out here
// that a long username could push off the edge.
//
// There is no tagline. "nine tiles, thirty seconds" sat beside the name on every
// screen including the board, where the tiles and the clock were saying it
// already, thirty times a game.
// ---------------------------------------------------------------------------

import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AccountMenu } from '../account/AccountMenu.js';

export interface AppHeaderProps {
  /** Sign-in override, passed straight through to the account menu. */
  onSignIn?: () => void;
  /**
   * Vetoes the brand link. Return false to keep the player where they are —
   * the room uses it to ask before abandoning a game in progress.
   *
   * It hangs off the click rather than replacing the `Link` with a button, so
   * the header still offers a real URL to middle-click, copy or open in a new
   * tab. A guard that answers false has stopped this navigation, not the link.
   *
   * It guards every way out of the header, not just the brand. The old account
   * cluster's profile link was outside it, which was survivable while it was
   * the only one and it was three words wide; the menu turned that into a list
   * of exits including "How to play", which is exactly the thing a player
   * reaches for mid-round and exactly the thing that must not throw the game
   * away. One guard, every link.
   */
  homeGuard?: () => boolean;
  /** Room-only controls, ahead of the avatar. */
  trailing?: ReactNode;
}

export function AppHeader({ onSignIn, homeGuard, trailing }: AppHeaderProps = {}) {
  const onBrandClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (homeGuard === undefined || homeGuard()) return;
    event.preventDefault();
  };

  return (
    <header className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3 sm:gap-3 sm:px-8">
      <Link
        to="/"
        onClick={onBrandClick}
        className="flex min-w-0 items-baseline gap-2.5 rounded-chip py-1 text-ink no-underline"
      >
        <span className="shrink-0 text-lg font-extrabold tracking-[-0.02em] sm:text-xl">
          Countdown
        </span>
      </Link>

      <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
        {trailing}
        <AccountMenu onSignIn={onSignIn} leaveGuard={homeGuard} />
      </div>
    </header>
  );
}
